import * as React from 'react';
import { useMemo, useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import {
    createTheme, CssBaseline, ThemeProvider,
    useMediaQuery, Box, IconButton, Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Menu, Brightness4, Brightness7, PersonOutlined } from '@mui/icons-material';

/* ── Page imports ── */
import Dashboard            from './pages/Dashboard';
import SupportPage from './pages/SupportPage';
import CardPage from './pages/CardPage';
import LoansPage from './pages/LoansPage';
import NotificationsPage from './pages/NotificationsPage';
import QrPage from "./pages/QrPage";
import InvestPage from './pages/InvestPage';
import SignUp               from './pages/SignUp';
import Landing              from './pages/Landing';
import SignIn               from './pages/SignIn';
import Goals                from './pages/GoalSetting';
import CreditScore          from './pages/SmartScoreAnalysis';
import TestCreds            from './pages/TestCreds';
import NGCreditLegal        from './pages/Ngcreditlegal';
import SendPage             from './pages/SendPage';
import SmartFinance         from './pages/SmartFinance';
import SpendingAnalysis     from './pages/SpendingAnalysis';
import DonutComparison      from './pages/DonutComparison';
import GoalSetting          from './pages/GoalSetting';
import UserProfilePage      from './pages/UserProfile';
import IncomeAnalysis       from './pages/IncomeAnalysis';
import Apply                from './pages/Apply';
import SharedNavBar         from './pages/components/SharedNavBar';
import WalletPage           from './pages/WalletPage';
import FundWalletPage       from './pages/FundWalletPage';
import WithdrawPage         from './pages/WithdrawPage';
import LearnSection         from './pages/LearnSection';
import AirtimePage          from './pages/AirtimePage';
import DataPage             from './pages/DataPage';
import BillsPage            from './pages/BillsPage';
import VerifyIdentity       from './pages/VerifyIdentity';
import TransactionHistoryPage from './pages/TransactionHistory';
import LinkBankPage         from './pages/LinkBankPage';
import ReferAndEarnPage     from './pages/Refer';
import MenuPage             from './pages/MenuPage';
import RewardsPage          from './pages/Rewards';
import DailyCheckIn         from './pages/DailyCheckIn';
import SpinAndWin           from './pages/SpinAndWin';
import HelpCentre           from './pages/HelpCentre';
import Raffle               from './pages/Raffle';
import Donate               from './pages/Donate';
import Invite               from './pages/Invite';
import DesktopLayout        from './pages/layouts/DesktopLayout';
import InvestmentsDashboard from './pages/cape/InvestmentsDashboard';
import MutualFundsSection   from './pages/cape/MutualFundsSection';
import MutualFundDetail from "./pages/MutualFundDetail";
import StockDetail from "./pages/StockDetail";
import WatchlistPage from "./pages/WatchlistPage";
import SavingsPage from "./pages/SavingsPage";
import InvestmentPlansPage from './pages/InvestmentPlansPage';
import PortfolioAnalyticsPage from "./pages/PortfolioAnalyticsPage";

const ScrollToTop: React.FC = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname]);
    return null;
};

/* ─────────────────────────────────────────────────────────────────────
   Pages that have their own full-bleed dark hero — the floating top bar
   overlays them (no paddingTop) and the "NG Credit" wordmark is hidden
   because the page already identifies itself in its own header.
───────────────────────────────────────────────────────────────────── */
const HERO_PAGES = ['/wallet', '/fund', '/transfer', '/withdraw'];

/* ─────────────────────────────────────────────────────────────────────
   Pages where the entire nav chrome (top bar + bottom nav) should be
   hidden — auth flows, landing, legal, standalone tools.
───────────────────────────────────────────────────────────────────── */
const HIDE_NAV_PATHS = ['/', '/signin', '/signup', '/apply', '/legal'];

const App: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme    = useTheme();

    const [mode, setMode] = useState<'light' | 'dark'>('light');
    const toggleMode = () => setMode(prev => prev === 'light' ? 'dark' : 'light');

    const isMobile  = useMediaQuery(theme.breakpoints.down('sm'));
    const hideNav   = HIDE_NAV_PATHS.includes(location.pathname);
    const isHeroPage = HERO_PAGES.includes(location.pathname);

    /* ── Theme ── */
    const createdTheme = useMemo(() => createTheme({
        palette: {
            mode,
            primary: {
                main: mode === 'light' ? '#634C9E' : '#7C5AC8',
                contrastText: '#ffffff',
            },
            secondary: { main: '#ffffff' },
            background: {
                default: mode === 'light' ? '#f9f9fb' : '#121212',
                paper:   mode === 'light' ? '#ffffff'  : '#1E1E1E',
            },
        },
    }), [mode]);

    /* ── Frosted pill style for floating icon buttons ── */
    const frostedBtn = {
        background:     alpha(mode === 'light' ? '#ffffff' : '#1A0F3D', 0.55),
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border:         `1px solid ${alpha(mode === 'light' ? '#000' : '#fff', 0.1)}`,
        color:          mode === 'light' ? '#1A0F3D' : '#fff',
        width: 38, height: 38,
        '&:hover': {
            background: alpha(mode === 'light' ? '#ffffff' : '#1A0F3D', 0.75),
        },
        transition: 'background .2s',
    } as const;

    return (
        <ThemeProvider theme={createdTheme}>
            <CssBaseline />
            <ScrollToTop />

            {/* ── Floating top bar (mobile only, hidden on auth pages) ── */}
            {!hideNav && isMobile && (
                <Box sx={{
                    position:  'fixed',
                    top: 0, left: 0, right: 0,
                    zIndex:    1100,
                    display:   'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5, py: 1,
                    /* fully transparent — pages paint their own backgrounds */
                    background:  'transparent',
                    pointerEvents: 'none',         // let clicks pass through empty areas
                }}>
                    {/* Left: Menu button + wordmark */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'auto' }}>
                        <IconButton onClick={() => navigate('/menu')} sx={frostedBtn}>
                            <Menu sx={{ fontSize: 18 }} />
                        </IconButton>

                        {/* Wordmark only shown on non-hero pages */}
                        {!isHeroPage && (
                            <Typography sx={{
                                fontWeight: 800,
                                fontSize: '.9rem',
                                letterSpacing: '-.02em',
                                color: mode === 'light' ? '#1A0F3D' : '#fff',
                                userSelect: 'none',
                            }}>
                                NG Credit
                            </Typography>
                        )}
                    </Box>

                    {/* Right: theme toggle */}
                    <Box sx={{ pointerEvents: 'auto' }}>
                        <IconButton onClick={toggleMode} sx={frostedBtn}>
                            {mode === 'dark'
                                ? <Brightness7 sx={{ fontSize: 18 }} />
                                : <Brightness4 sx={{ fontSize: 18 }} />}
                        </IconButton>

                    </Box>
                </Box>
            )}

            {/* ── Bottom nav bar ── */}
            {!hideNav && (
                <SharedNavBar onToggleTheme={toggleMode} />
            )}

            {/* ── Page content ── */}
            <Box sx={{
                /*
                 * Hero pages (wallet, fund, send, withdraw) manage their own
                 * internal top padding — the overlay bar floats above them.
                 * All other mobile pages need 64px clearance for the fixed bar.
                 */
                paddingTop: isMobile && !hideNav && !isHeroPage ? '64px' : 0,
                /*
                 * Bottom padding clears the bottom nav bar on mobile for all
                 * authenticated pages.
                 */
                paddingBottom: isMobile && !hideNav ? '64px' : 0,
            }}>
                <Routes>
                    {/* ── Public / auth routes (no layout wrapper) ── */}
                    <Route path="/"         element={<Landing />} />
                    <Route path="/legal"    element={<NGCreditLegal />} />
                    <Route path="/apply"    element={<Apply />} />
                    <Route path="/demo"     element={<VerifyIdentity />} />
                    <Route path="/signup"   element={<SignUp />} />
                    <Route path="/signin"   element={<SignIn />} />

                    {/* Cape standalone tools */}
                    <Route path="/cape/investments" element={<InvestmentsDashboard />} />
                    <Route path="/cape/mf"          element={<MutualFundsSection />} />
                    <Route path="/cape/mf-plus"     element={<MutualFundsSection />} />

                    {/* ── Authenticated routes (3-column desktop layout) ── */}
                    <Route element={<DesktopLayout />}>
                        <Route path="/dashboard"        element={<Dashboard />} />
                        <Route path="/wallet"           element={<WalletPage />} />
                        <Route path="/goals"            element={<GoalSetting />} />
                        <Route path="/profile"          element={<UserProfilePage />} />
                        <Route path="/donut"            element={<DonutComparison />} />
                        <Route path="/income-analysis"  element={<IncomeAnalysis />} />
                        <Route path="/spending-analysis" element={<SpendingAnalysis />} />

                        {/* Wallet actions */}
                        <Route path="/fund"             element={<FundWalletPage />} />
                        <Route path="/withdraw"         element={<WithdrawPage />} />
                        <Route path="/transfer"         element={<SendPage />} />
                        <Route path="/support"         element={<SupportPage />} />
                        <Route path="/card"         element={<CardPage />} />
                        <Route path="/loans"         element={<LoansPage />} />
                        <Route path="/notifications"         element={<NotificationsPage />} />
                        <Route path="/qr"         element={<QrPage />} />

                        {/* Invest */}
                        <Route path="/invest"             element={<InvestPage />} />
                        <Route path="/invest/fund/:id"   element={<MutualFundDetail />} />
                        <Route path="/invest/stock/:id"  element={<StockDetail />} />
                        <Route path="/savings"              element={<SavingsPage />} />
                        <Route path="/invest/watchlist"              element={<WatchlistPage />} />
                        <Route path="/invest/analytics"     element={<PortfolioAnalyticsPage />} />
                        <Route path="/invest/plans"         element={<InvestmentPlansPage />} />



                        {/* Services */}
                        <Route path="/learn"            element={<LearnSection />} />
                        <Route path="/airtime"          element={<AirtimePage />} />
                        <Route path="/datapage"         element={<DataPage />} />
                        <Route path="/bills"            element={<BillsPage />} />
                        <Route path="/history"          element={<TransactionHistoryPage />} />
                        <Route path="/link-bank"        element={<LinkBankPage />} />
                        <Route path="/refer"            element={<ReferAndEarnPage />} />
                        <Route path="/menu"             element={<MenuPage />} />

                        {/* Rewards */}
                        <Route path="/rewards"                  element={<RewardsPage />} />
                        <Route path="/rewards/daily-checkin"    element={<DailyCheckIn />} />
                        <Route path="/rewards/spin"             element={<SpinAndWin />} />
                        <Route path="/raffle"                   element={<Raffle />} />

                        {/* Misc */}
                        <Route path="/donate"  element={<Donate />} />
                        <Route path="/help"    element={<HelpCentre />} />
                        <Route path="/invite"  element={<Invite />} />
                    </Route>

                    {/* ── Standalone tools (no layout wrapper) ── */}
                    <Route path="/cc"   element={<CreditScore />} />
                    <Route path="/risk" element={<Goals />} />
                    <Route path="/data" element={<TestCreds />} />
                    <Route path="/pfm"  element={<SmartFinance />} />
                </Routes>
            </Box>
        </ThemeProvider>
    );
};

export default App;